import { TSTitleCard } from '@/components/shared/card/Card';
import { useEmployeesSkillsList } from '@/pages/roster/hooks/useEmployeeSkillsList';
import { ReadEmployee } from '@/services/gql/graphql.generated';
import { useTasks } from '@/services/tasks/useTasks';
import { FunctionComponent, useMemo } from 'react';
import { FiClipboard, FiStar } from 'react-icons/fi';
import strings from '../../team.translations';
import TeanMemberTaskChit from './TeamMemberTaskChit';
import { useNavigate } from 'react-router-dom';
import { useActiveSkills } from '@/services/tasks/useSkills';

export const skillsSectionId = 'skills-section-id';

interface TeamMemberSkillsProps {
  employee: ReadEmployee;
}

const TeamMemberSkills: FunctionComponent<TeamMemberSkillsProps> = ({ employee }) => {
  const employeeSkillsObj = useEmployeesSkillsList([employee.identityId]);
  const navigate = useNavigate();

  const activeSkills = useActiveSkills();
  const activeSkillIds = activeSkills.map(s => s.id);

  const skills = employeeSkillsObj[0].skills;

  const skillIds = skills.map(s => s.skillId);

  const tasks = useTasks();

  const assignableTasks = useMemo(() => {
    return tasks.filter(
      task =>
        task.isActive &&
        task.skills.filter(s => activeSkillIds.includes(s)).every(requiredSkillId => skillIds.includes(requiredSkillId))
    );
  }, [tasks, activeSkillIds]);

  const nonAssignableTasks = useMemo(() => {
    return tasks.filter(task => task.isActive && !assignableTasks.find(t => t.id === task.id));
  }, [tasks, assignableTasks]);

  const onEdit = () => {
    navigate(`/team-member/${employee.identityId}/set-skills`);
  };
  return (
    <section id={skillsSectionId} className="flex flex-col gap-4">
      <TSTitleCard
        primaryAction={{
          label: strings.skillsCard.edit,
          onClick: onEdit
        }}
        title={strings.skillsCard.title}
        icon={FiStar}
      >
        {skills.length === 0 ? (
          <p className="text-center font-semibold italic">{strings.skillsCard.noData}</p>
        ) : (
          <div className="flex flex-col space-y-2">
            <p>{strings.skillsCard.assignedSkills}</p>
            <div className="flex flex-wrap gap-4">
              {skills.map(s => (
                <div className="bg-primary-200 p-2 border border-primary-600 rounded-md flex flex-row space-x-2 items-center">
                  <FiStar className="w-5 h-5 text-primary-600 shrink-0" />
                  <p className="text-sm font-semibold text-primary-600">{s.name}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </TSTitleCard>

      {tasks.length > 0 && (
        <TSTitleCard title={strings.taskCard.title} icon={FiClipboard}>
          {skills.length === 0 ? (
            <p className="text-sm italic text-secondary-std">{strings.taskCard.noSkills(employee.firstName)}</p>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-col space-y-2">
                <p className="text-lg font-semibold">{strings.taskCard.assignableTasks}</p>
                {assignableTasks.length === 0 ? (
                  <p className="text-sm italic">{strings.taskCard.noAssignableTasks(employee.firstName)}</p>
                ) : (
                  <>
                    <p>{strings.taskCard.assignableTasksInfo(employee.firstName)}</p>
                    <div className="flex flex-wrap gap-4">
                      {assignableTasks.map(t => (
                        <TeanMemberTaskChit key={t.id} task={t} />
                      ))}
                    </div>
                  </>
                )}
              </div>
              <div className="flex flex-col space-y-2">
                <p className="text-lg font-semibold">{strings.taskCard.nonAssignableTasks}</p>
                {nonAssignableTasks.length === 0 ? (
                  <p className="text-sm italic">{strings.taskCard.noNonAssignableTasks(employee.firstName)}</p>
                ) : (
                  <>
                    <p>{strings.taskCard.nonAssignableTasksInfo(employee.firstName)}</p>
                    <div className="flex flex-wrap gap-4">
                      {nonAssignableTasks.map(t => (
                        <TeanMemberTaskChit key={t.id} task={t} />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </TSTitleCard>
      )}
    </section>
  );
};

export default TeamMemberSkills;
